import { Injectable, inject } from '@angular/core';
import { SiteService as TrackerSiteService, TrackerBatchRequest } from '@wheere/api-tracker';
import { errorCode, errorMessage } from '@wheere-front/shared-util';
import { lastValueFrom } from 'rxjs';
import {
  BatchExecutor,
  TrackerBatchCall,
  TrackerBatchOutcome,
  TrackerBatchParams,
  chunkTrackerIds,
  mergeBatchOutcomes,
} from '../util/tracker-batch';

/**
 * Le seul endroit qui appelle `POST /trackers/batch/site` (flux 12) : Manage et la fiche site
 * passent tous les deux par ici. Le serveur traite un lot de 1 à 200 trackers en un appel ; au-delà,
 * les paquets partent l'un après l'autre et les résultats sont fusionnés.
 *
 * Erreur globale (400 VALIDATION_ERROR, 403 ACCESS_DENIED…) sur le premier paquet : rejetée telle
 * quelle, l'appelant la montre avec `errorMessage`. Sur un paquet suivant, une partie du lot est
 * déjà faite : les trackers de ce paquet sortent en échec avec le code et le message du serveur,
 * pour que le récapitulatif reste juste.
 */
@Injectable({ providedIn: 'root' })
export class TrackerBatchService {
  readonly #api = inject(TrackerSiteService);

  async run(call: TrackerBatchCall): Promise<TrackerBatchOutcome> {
    const outcomes: TrackerBatchOutcome[] = [];
    const chunks = chunkTrackerIds(call.trackerIds);
    for (let i = 0; i < chunks.length; i++) {
      const trackerIds = chunks[i];
      try {
        const res = await lastValueFrom(this.#api.batchTrackersSite(this.#body(call, trackerIds)));
        outcomes.push({ succeeded: res.succeeded ?? [], failed: res.failed ?? [] });
      } catch (e) {
        if (i === 0) throw e;
        const code = errorCode(e) ?? 'INTERNAL_ERROR';
        const message = errorMessage(e, code);
        outcomes.push({ succeeded: [], failed: trackerIds.map(trackerId => ({ trackerId, code, message })) });
      }
    }
    return mergeBatchOutcomes(outcomes);
  }

  /** Lot à action fixée, pour le récapitulatif (`TrackerBatchRunComponent.execute`). */
  executor(params: TrackerBatchParams): BatchExecutor {
    return (trackerIds, stopLogging) => this.run({ ...params, trackerIds, stopLogging });
  }

  #body(call: TrackerBatchCall, trackerIds: string[]): TrackerBatchRequest {
    const body: TrackerBatchRequest = { action: call.action, trackerIds };
    if (call.siteId) body.siteId = call.siteId;
    if (call.profileId) body.profileId = call.profileId;
    if (call.organizationId) body.organizationId = call.organizationId;
    body.stopLogging = call.stopLogging;
    return body;
  }
}
